import axios from 'axios';
import { toast } from 'sonner';

export function isTokenExpired(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (!payload.exp) return false;
    return Date.now() >= payload.exp * 1000;
  } catch (e) {
    return true;
  }
}

export function clearSession() {
  localStorage.removeItem('business_token');
  localStorage.removeItem('business_user');
}

let sessionExpiredHandled = false;
let expiryTimerId = null;

function handleSessionExpired(language) {
  if (sessionExpiredHandled) return;
  sessionExpiredHandled = true;
  clearSession();
  window.dispatchEvent(new Event('userLoggedOut'));
  toast.error(
    language === 'en'
      ? 'Your session has expired. Please log in again.'
      : 'انتهت جلسة تسجيل الدخول. يرجى تسجيل الدخول من جديد.'
  );
  setTimeout(() => {
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    } else {
      window.location.reload();
    }
  }, 800);
}

/**
 * Proactive expiry watcher — schedules an auto-logout for the EXACT moment the
 * current token expires, even if the user is idle and not making API calls.
 * Also runs a lightweight tab-focus check to catch clock skew or system sleep.
 */
function scheduleAutoLogout() {
  // Clear any previous timer
  if (expiryTimerId) {
    clearTimeout(expiryTimerId);
    expiryTimerId = null;
  }
  const token = localStorage.getItem('business_token');
  if (!token) return;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (!payload.exp) return;
    const msUntilExpiry = payload.exp * 1000 - Date.now();
    const language = localStorage.getItem('cloudgm_language') || 'ar';
    if (msUntilExpiry <= 0) {
      handleSessionExpired(language);
      return;
    }
    // Cap the timeout to a safe max (setTimeout wraps at ~24.8 days) — token is 7d max anyway
    const safeMs = Math.min(msUntilExpiry, 7 * 24 * 60 * 60 * 1000);
    expiryTimerId = setTimeout(() => {
      handleSessionExpired(language);
    }, safeMs);
  } catch (e) {
    // Malformed token — force logout
    const language = localStorage.getItem('cloudgm_language') || 'ar';
    handleSessionExpired(language);
  }
}

export function setupAuthInterceptors() {
  // Before each request: if stored token already expired, log out immediately
  axios.interceptors.request.use((config) => {
    const token = localStorage.getItem('business_token');
    if (token && isTokenExpired(token)) {
      const language = localStorage.getItem('cloudgm_language') || 'ar';
      handleSessionExpired(language);
      return Promise.reject(new axios.Cancel('session-expired'));
    }
    return config;
  });

  // On any 401 response (except auth endpoints themselves): log out + redirect to login
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const url = error?.config?.url || '';
      const isAuthCall = url.includes('/auth/') || url.includes('/verification/');
      const hadToken = !!localStorage.getItem('business_token');
      if (status === 401 && !isAuthCall && hadToken) {
        const language = localStorage.getItem('cloudgm_language') || 'ar';
        handleSessionExpired(language);
      }
      return Promise.reject(error);
    }
  );

  // Kick off the proactive expiry watcher for the currently stored token
  scheduleAutoLogout();

  // Re-schedule whenever login/logout events fire (login refreshes token, logout clears it)
  window.addEventListener('userLoggedIn', () => {
    sessionExpiredHandled = false;
    scheduleAutoLogout();
  });
  window.addEventListener('userLoggedOut', () => {
    if (expiryTimerId) {
      clearTimeout(expiryTimerId);
      expiryTimerId = null;
    }
  });

  // Safety net: when the tab regains focus (e.g. after laptop sleep), re-check
  // in case setTimeout was paused/delayed by the browser.
  const recheckOnFocus = () => {
    const token = localStorage.getItem('business_token');
    if (token && isTokenExpired(token)) {
      const language = localStorage.getItem('cloudgm_language') || 'ar';
      handleSessionExpired(language);
    }
  };
  window.addEventListener('focus', recheckOnFocus);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') recheckOnFocus();
  });
}
